/**
 * Grobes Raster für Bot-Wegfindung (BFS). Wird nach Hallengenerierung gebaut.
 */
import type { Vec } from './PlayerControl';

export type PathGrid = {
  cell: number;
  cols: number;
  rows: number;
  solid: Uint8Array;
};

export function buildPathGrid(
  worldW: number,
  worldH: number,
  cell: number,
  isSolidWorld: (wx: number, wy: number) => boolean,
): PathGrid {
  const cols = Math.ceil(worldW / cell);
  const rows = Math.ceil(worldH / cell);
  const solid = new Uint8Array(cols * rows);
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const wx = x * cell + cell / 2;
      const wy = y * cell + cell / 2;
      solid[y * cols + x] = isSolidWorld(wx, wy) ? 1 : 0;
    }
  }
  return { cell, cols, rows, solid };
}

function key(cols: number, x: number, y: number): number {
  return y * cols + x;
}

/** Nächster Schritt (Weltmitte der Nachbarzelle) Richtung Ziel, oder null. */
export function pathStep(grid: PathGrid, from: Vec, to: Vec): Vec | null {
  const { cell, cols, rows, solid } = grid;
  const sx = Math.max(0, Math.min(cols - 1, (from.x / cell) | 0));
  const sy = Math.max(0, Math.min(rows - 1, (from.y / cell) | 0));
  const gx = Math.max(0, Math.min(cols - 1, (to.x / cell) | 0));
  const gy = Math.max(0, Math.min(rows - 1, (to.y / cell) | 0));
  if (sx === gx && sy === gy) return null;

  const start = key(cols, sx, sy);
  const goal = key(cols, gx, gy);
  if (solid[goal]) {
    // nächste freie Zelle um Ziel
    for (const [dx, dy] of [
      [0, 0],
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
      [1, 1],
      [-1, -1],
      [1, -1],
      [-1, 1],
    ] as [number, number][]) {
      const nx = gx + dx;
      const ny = gy + dy;
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
      if (!solid[key(cols, nx, ny)]) {
        return pathStep(grid, from, { x: nx * cell + cell / 2, y: ny * cell + cell / 2 });
      }
    }
    return null;
  }

  const came = new Int32Array(cols * rows).fill(-1);
  const q = new Int32Array(cols * rows);
  let qh = 0;
  let qt = 0;
  q[qt++] = start;
  came[start] = start;
  const dirs: [number, number][] = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ];

  let found = false;
  while (qh < qt) {
    const cur = q[qh++]!;
    if (cur === goal) {
      found = true;
      break;
    }
    const cx = cur % cols;
    const cy = (cur / cols) | 0;
    for (const [dx, dy] of dirs) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
      const nk = key(cols, nx, ny);
      if (came[nk] !== -1) continue;
      if (solid[nk] && nk !== goal) continue;
      came[nk] = cur;
      q[qt++] = nk;
    }
  }
  if (!found) return null;

  // zurückverfolgen bis ein Schritt vom Start
  let cur = goal;
  let prev = came[cur]!;
  while (prev !== start && prev !== -1) {
    cur = prev;
    prev = came[cur]!;
  }
  const cx = cur % cols;
  const cy = (cur / cols) | 0;
  return { x: cx * cell + cell / 2, y: cy * cell + cell / 2 };
}
